import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  CheckCircle2, 
  AlertCircle, 
  Settings as SettingsIcon,
  Key,
  Building2,
  Mic,
  Globe,
  DollarSign,
  Percent,
  Languages,
  MapPin,
  Phone,
  Mail,
  FileText
} from 'lucide-react';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { isModuleEnabled } from '../config/branding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Currency options with symbols - Using new official SAR symbol (ر.س)
const currencies = [
  { code: 'SAR', symbol: 'ر.س', name: 'Saudi Riyal', nameAr: 'ريال سعودي' },
  { code: 'USD', symbol: '$', name: 'US Dollar', nameAr: 'دولار أمريكي' },
  { code: 'EUR', symbol: '€', name: 'Euro', nameAr: 'يورو' },
  { code: 'GBP', symbol: '£', name: 'British Pound', nameAr: 'جنيه إسترليني' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', nameAr: 'درهم إماراتي' },
  { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar', nameAr: 'دينار كويتي' },
  { code: 'QAR', symbol: 'ر.ق', name: 'Qatari Riyal', nameAr: 'ريال قطري' },
  { code: 'BHD', symbol: 'د.ب', name: 'Bahraini Dinar', nameAr: 'دينار بحريني' },
  { code: 'OMR', symbol: 'ر.ع', name: 'Omani Rial', nameAr: 'ريال عماني' },
  { code: 'EGP', symbol: 'ج.م', name: 'Egyptian Pound', nameAr: 'جنيه مصري' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', nameAr: 'روبية هندية' },
  { code: 'PKR', symbol: 'Rs', name: 'Pakistani Rupee', nameAr: 'روبية باكستانية' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', nameAr: 'يوان صيني' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', nameAr: 'ين ياباني' },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira', nameAr: 'ليرة تركية' },
  { code: 'JOD', symbol: 'د.أ', name: 'Jordanian Dinar', nameAr: 'دينار أردني' },
  { code: 'LBP', symbol: 'ل.ل', name: 'Lebanese Pound', nameAr: 'ليرة لبنانية' },
];

// Region to default settings mapping
const regionDefaults = {
  'SA': { currency: 'SAR', tax_rate: 15, tax_name: 'VAT', timezone: 'Asia/Riyadh' },
  'AE': { currency: 'AED', tax_rate: 5, tax_name: 'VAT', timezone: 'Asia/Dubai' },
  'KW': { currency: 'KWD', tax_rate: 0, tax_name: 'None', timezone: 'Asia/Kuwait' },
  'QA': { currency: 'QAR', tax_rate: 0, tax_name: 'None', timezone: 'Asia/Qatar' },
  'BH': { currency: 'BHD', tax_rate: 10, tax_name: 'VAT', timezone: 'Asia/Bahrain' },
  'OM': { currency: 'OMR', tax_rate: 5, tax_name: 'VAT', timezone: 'Asia/Muscat' },
  'EG': { currency: 'EGP', tax_rate: 14, tax_name: 'VAT', timezone: 'Africa/Cairo' },
  'JO': { currency: 'JOD', tax_rate: 16, tax_name: 'Sales Tax', timezone: 'Asia/Amman' },
  'LB': { currency: 'LBP', tax_rate: 11, tax_name: 'VAT', timezone: 'Asia/Beirut' },
  'US': { currency: 'USD', tax_rate: 0, tax_name: 'Sales Tax', timezone: 'America/New_York' },
  'GB': { currency: 'GBP', tax_rate: 20, tax_name: 'VAT', timezone: 'Europe/London' },
  'DE': { currency: 'EUR', tax_rate: 19, tax_name: 'VAT', timezone: 'Europe/Berlin' },
  'FR': { currency: 'EUR', tax_rate: 20, tax_name: 'VAT', timezone: 'Europe/Paris' },
  'IN': { currency: 'INR', tax_rate: 18, tax_name: 'GST', timezone: 'Asia/Kolkata' },
  'PK': { currency: 'PKR', tax_rate: 17, tax_name: 'Sales Tax', timezone: 'Asia/Karachi' },
  'TR': { currency: 'TRY', tax_rate: 18, tax_name: 'VAT', timezone: 'Europe/Istanbul' },
};

// Region/Country options
const regions = [
  { code: 'SA', name: 'Saudi Arabia', nameAr: 'المملكة العربية السعودية' },
  { code: 'AE', name: 'United Arab Emirates', nameAr: 'الإمارات العربية المتحدة' },
  { code: 'KW', name: 'Kuwait', nameAr: 'الكويت' },
  { code: 'QA', name: 'Qatar', nameAr: 'قطر' },
  { code: 'BH', name: 'Bahrain', nameAr: 'البحرين' },
  { code: 'OM', name: 'Oman', nameAr: 'عمان' },
  { code: 'EG', name: 'Egypt', nameAr: 'مصر' },
  { code: 'JO', name: 'Jordan', nameAr: 'الأردن' },
  { code: 'LB', name: 'Lebanon', nameAr: 'لبنان' },
  { code: 'US', name: 'United States', nameAr: 'الولايات المتحدة' },
  { code: 'GB', name: 'United Kingdom', nameAr: 'المملكة المتحدة' },
  { code: 'DE', name: 'Germany', nameAr: 'ألمانيا' },
  { code: 'FR', name: 'France', nameAr: 'فرنسا' },
  { code: 'IN', name: 'India', nameAr: 'الهند' },
  { code: 'PK', name: 'Pakistan', nameAr: 'باكستان' },
  { code: 'TR', name: 'Turkey', nameAr: 'تركيا' },
];

// Language options - filtered based on module settings
const getLanguageOptions = () => {
  const allLanguages = [
    { code: 'en', name: 'English', nameAr: 'الإنجليزية' },
    { code: 'ar', name: 'Arabic (العربية)', nameAr: 'العربية' },
  ];
  
  // Filter out Arabic if arabicLanguage module is disabled
  if (!isModuleEnabled('arabicLanguage')) {
    return allLanguages.filter(l => l.code !== 'ar');
  }
  return allLanguages;
};

const languages = getLanguageOptions();

// Timezone options
const timezones = [
  { code: 'Asia/Riyadh', name: 'Arabia Standard Time (Riyadh)', offset: '+03:00' },
  { code: 'Asia/Dubai', name: 'Gulf Standard Time (Dubai)', offset: '+04:00' },
  { code: 'Asia/Kuwait', name: 'Arabia Standard Time (Kuwait)', offset: '+03:00' },
  { code: 'Asia/Qatar', name: 'Arabia Standard Time (Qatar)', offset: '+03:00' },
  { code: 'Asia/Bahrain', name: 'Arabia Standard Time (Bahrain)', offset: '+03:00' },
  { code: 'Asia/Muscat', name: 'Gulf Standard Time (Muscat)', offset: '+04:00' },
  { code: 'Africa/Cairo', name: 'Eastern European Time (Cairo)', offset: '+02:00' },
  { code: 'Asia/Amman', name: 'Eastern European Time (Amman)', offset: '+03:00' },
  { code: 'Asia/Beirut', name: 'Eastern European Time (Beirut)', offset: '+02:00' },
  { code: 'UTC', name: 'Coordinated Universal Time (UTC)', offset: '+00:00' },
  { code: 'Europe/London', name: 'Greenwich Mean Time (London)', offset: '+00:00' },
  { code: 'Europe/Berlin', name: 'Central European Time (Berlin)', offset: '+01:00' },
  { code: 'Europe/Paris', name: 'Central European Time (Paris)', offset: '+01:00' },
  { code: 'Europe/Istanbul', name: 'Turkey Time (Istanbul)', offset: '+03:00' },
  { code: 'America/New_York', name: 'Eastern Time (New York)', offset: '-05:00' },
  { code: 'America/Los_Angeles', name: 'Pacific Time (Los Angeles)', offset: '-08:00' },
  { code: 'Asia/Kolkata', name: 'India Standard Time (Mumbai)', offset: '+05:30' },
  { code: 'Asia/Karachi', name: 'Pakistan Standard Time (Karachi)', offset: '+05:00' },
];

const Settings = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { refetchSettings } = useSettings();

  // API Keys State
  const [keys, setKeys] = useState({
    retell_api_key: '',
    openai_api_key: ''
  });
  const [status, setStatus] = useState({ retell_key_set: false, openai_key_set: false });
  const [loading, setLoading] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);

  // Company Settings State
  const [companySettings, setCompanySettings] = useState({
    company_name: '',
    company_name_ar: '',
    industry: 'technology',
    region: 'SA',
    currency: 'SAR',
    tax_rate: 15,
    tax_name: 'VAT',
    tax_name_ar: 'ضريبة القيمة المضافة',
    tax_state_code: '',
    language: 'en',
    document_language: 'bilingual_en',
    timezone: 'Asia/Riyadh',
    address: '',
    address_ar: '',
    city: '',
    city_ar: '',
    postal_code: '',
    phone: '',
    email: '',
    website: '',
    tax_number: '',
    cr_number: '',
    logo_url: '',
    terms_conditions: '',
    terms_conditions_ar: '',
  });

  useEffect(() => {
    fetchSettings();
    fetchCompanySettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings/`);
      setStatus(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/settings/company`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data) {
        setCompanySettings(prev => ({ ...prev, ...res.data }));
      }
    } catch (err) {
      console.error('No company settings found, using defaults');
    }
  };

  const handleSaveKeys = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/settings/update`, keys);
      toast.success(isRTL ? "تم تحديث مفاتيح API" : "API Keys Updated");
      fetchSettings();
      setKeys({ retell_api_key: '', openai_api_key: '' });
    } catch (err) {
      toast.error(isRTL ? "فشل في تحديث الإعدادات" : "Failed to update settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompanySettings = async () => {
    setSavingCompany(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/settings/company`, companySettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(isRTL ? "تم حفظ إعدادات الشركة" : "Company settings saved successfully");
      
      // Refetch global settings context to update currency everywhere
      await refetchSettings();
      
      // Update language if changed
      if (companySettings.language !== i18n.language) {
        i18n.changeLanguage(companySettings.language);
        // Update document direction for RTL support
        document.documentElement.dir = companySettings.language === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = companySettings.language;
        if (companySettings.language === 'ar') {
          document.documentElement.classList.add('font-arabic');
        } else {
          document.documentElement.classList.remove('font-arabic');
        }
      }
    } catch (err) {
      toast.error(isRTL ? "فشل في حفظ الإعدادات" : "Failed to save company settings");
    } finally {
      setSavingCompany(false);
    }
  };

  // US State Tax data
  const [usaStates, setUsaStates] = useState([]);

  useEffect(() => {
    if (companySettings.region === 'US') {
      axios.get(`${API_URL}/api/pos/tax/states`).then(res => setUsaStates(res.data)).catch(() => {});
    }
  }, [companySettings.region]);

  const handleStateTaxChange = (stateCode) => {
    const state = usaStates.find(s => s.code === stateCode);
    if (state) {
      setCompanySettings(prev => ({
        ...prev,
        tax_state_code: stateCode,
        tax_rate: state.rate,
        tax_name: 'Sales Tax',
      }));
    }
  };

  const updateCompanySetting = (key, value) => {
    setCompanySettings(prev => ({ ...prev, [key]: value }));
  };

  // Handle region change - auto-populate currency, tax, and timezone
  const handleRegionChange = (regionCode) => {
    const defaults = regionDefaults[regionCode];
    if (defaults) {
      setCompanySettings(prev => ({
        ...prev,
        region: regionCode,
        currency: defaults.currency,
        tax_rate: defaults.tax_rate,
        tax_name: defaults.tax_name,
        timezone: defaults.timezone
      }));
      toast.info(isRTL ? 'تم تحديث العملة والضريبة تلقائياً' : 'Currency and Tax updated automatically');
    } else {
      setCompanySettings(prev => ({ ...prev, region: regionCode }));
    }
  };

  // Get currency display based on selected language setting
  // English: codes (SAR, AED, USD)
  // Arabic: symbols (ر.س, د.إ, $)
  const getCurrencyDisplay = (code) => {
    const currency = currencies.find(c => c.code === code);
    if (!currency) return code;
    
    // Use selected language setting (not current UI language)
    if (companySettings.language === 'ar') {
      return currency.symbol; // Arabic symbols
    }
    return code; // Currency codes for English
  };

  const getCurrencySymbol = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency ? currency.symbol : code;
  };

  return (
    <div className={`space-y-6 ${isRTL ? 'rtl' : ''}`}>
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <SettingsIcon className="h-8 w-8" />
          {isRTL ? 'الإعدادات' : 'Settings'}
        </h1>
        <p className="text-muted-foreground">
          {isRTL ? 'تكوين إعدادات النظام والتكاملات وإدارة المستخدمين' : 'Configure system settings, API integrations, and manage users.'}
        </p>
      </div>

      <Tabs defaultValue="company" className="space-y-6">
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="company" className="gap-2">
            <Building2 className="h-4 w-4" /> {isRTL ? 'الشركة' : 'Company'}
          </TabsTrigger>
          <TabsTrigger value="regional" className="gap-2">
            <Globe className="h-4 w-4" /> {isRTL ? 'الإقليمية' : 'Regional'}
          </TabsTrigger>
          {isModuleEnabled('aiVoiceApi') && (
            <TabsTrigger value="api" className="gap-2">
              <Key className="h-4 w-4" /> {isRTL ? 'AI API' : 'AI Voice API'}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Company Settings */}
        <TabsContent value="company">
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Building2 className="h-5 w-5 text-primary" /> 
                {isRTL ? 'معلومات الشركة' : 'Company Information'}
              </CardTitle>
              <CardDescription>
                {isRTL ? 'المعلومات الأساسية لشركتك' : 'Basic information about your organization.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isRTL ? 'اسم الشركة (إنجليزي)' : 'Company Name (English)'}</Label>
                  <Input 
                    value={companySettings.company_name}
                    onChange={e => updateCompanySetting('company_name', e.target.value)}
                    placeholder="FlowFeed Technologies"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'اسم الشركة (عربي)' : 'Company Name (Arabic)'}</Label>
                  <Input 
                    value={companySettings.company_name_ar}
                    onChange={e => updateCompanySetting('company_name_ar', e.target.value)}
                    placeholder="فلو فيد للتقنية"
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'الصناعة' : 'Industry'}</Label>
                  <Select value={companySettings.industry} onValueChange={v => updateCompanySetting('industry', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="technology">{isRTL ? 'التكنولوجيا' : 'Technology'}</SelectItem>
                      <SelectItem value="telecom">{isRTL ? 'الاتصالات' : 'Telecommunications'}</SelectItem>
                      <SelectItem value="construction">{isRTL ? 'البناء' : 'Construction'}</SelectItem>
                      <SelectItem value="finance">{isRTL ? 'المالية' : 'Finance'}</SelectItem>
                      <SelectItem value="healthcare">{isRTL ? 'الرعاية الصحية' : 'Healthcare'}</SelectItem>
                      <SelectItem value="retail">{isRTL ? 'التجزئة' : 'Retail'}</SelectItem>
                      <SelectItem value="manufacturing">{isRTL ? 'التصنيع' : 'Manufacturing'}</SelectItem>
                      <SelectItem value="other">{isRTL ? 'أخرى' : 'Other'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'رقم السجل التجاري' : 'Commercial Registration (CR)'}</Label>
                  <Input 
                    value={companySettings.cr_number}
                    onChange={e => updateCompanySetting('cr_number', e.target.value)}
                    placeholder="1010XXXXXX"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Phone className="h-4 w-4" /> {isRTL ? 'الهاتف' : 'Phone'}</Label>
                  <Input 
                    value={companySettings.phone}
                    onChange={e => updateCompanySetting('phone', e.target.value)}
                    placeholder="+966 XX XXX XXXX"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Mail className="h-4 w-4" /> {isRTL ? 'البريد الإلكتروني' : 'Email'}</Label>
                  <Input 
                    type="email"
                    value={companySettings.email}
                    onChange={e => updateCompanySetting('email', e.target.value)}
                    placeholder="info@company.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Globe className="h-4 w-4" /> {isRTL ? 'الموقع الإلكتروني' : 'Website'}</Label>
                  <Input 
                    value={companySettings.website}
                    onChange={e => updateCompanySetting('website', e.target.value)}
                    placeholder="https://www.company.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {isRTL ? 'المدينة' : 'City'}</Label>
                  <Input 
                    value={companySettings.city}
                    onChange={e => updateCompanySetting('city', e.target.value)}
                    placeholder={isRTL ? 'الرياض' : 'Riyadh'}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{isRTL ? 'العنوان' : 'Address'}</Label>
                <Textarea 
                  value={companySettings.address}
                  onChange={e => updateCompanySetting('address', e.target.value)}
                  placeholder={isRTL ? 'العنوان الكامل...' : 'Full address...'}
                  rows={2}
                />
              </div>

              <div className={`flex pt-4 border-t ${isRTL ? 'justify-start' : 'justify-end'}`}>
                <Button onClick={handleSaveCompanySettings} disabled={savingCompany}>
                  {savingCompany ? (isRTL ? 'جاري الحفظ...' : 'Saving...') : (isRTL ? 'حفظ التغييرات' : 'Save Changes')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Regional Settings */}
        <TabsContent value="regional">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Region & Language */}
            <Card>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Globe className="h-5 w-5 text-primary" /> 
                  {isRTL ? 'المنطقة واللغة' : 'Region & Language'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {isRTL ? 'الدولة/المنطقة' : 'Country/Region'}</Label>
                  <Select value={companySettings.region} onValueChange={handleRegionChange}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {regions.map(r => (
                        <SelectItem key={r.code} value={r.code}>
                          {isRTL ? r.nameAr : r.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {isRTL ? 'اختيار الدولة سيحدث العملة والضريبة تلقائياً' : 'Selecting a country will auto-update currency and tax'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Languages className="h-4 w-4" /> {isRTL ? 'اللغة' : 'Language'}</Label>
                  <Select value={companySettings.language} onValueChange={v => updateCompanySetting('language', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {languages.map(l => (
                        <SelectItem key={l.code} value={l.code}>
                          {isRTL ? l.nameAr : l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{isRTL ? 'المنطقة الزمنية' : 'Timezone'}</Label>
                  <Select value={companySettings.timezone} onValueChange={v => updateCompanySetting('timezone', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {timezones.map(tz => (
                        <SelectItem key={tz.code} value={tz.code}>
                          {tz.name} ({tz.offset})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Currency & Tax */}
            <Card>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <DollarSign className="h-5 w-5 text-primary" /> 
                  {isRTL ? 'العملة والضريبة' : 'Currency & Tax'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><DollarSign className="h-4 w-4" /> {isRTL ? 'العملة' : 'Currency'}</Label>
                  <Select value={companySettings.currency} onValueChange={v => updateCompanySetting('currency', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {currencies.map(c => (
                        <SelectItem key={c.code} value={c.code}>
                          <span className="flex items-center gap-2">
                            <span className="font-mono text-lg">{c.symbol}</span>
                            <span>{c.code} - {isRTL ? c.nameAr : c.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {isRTL ? 'الرمز المحدد:' : 'Selected symbol:'} <span className="font-mono text-lg font-bold">{getCurrencySymbol(companySettings.currency)}</span>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Percent className="h-4 w-4" /> {isRTL ? 'نسبة الضريبة (%)' : 'Tax Rate (%)'}</Label>
                  <Input 
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={companySettings.tax_rate}
                    onChange={e => updateCompanySetting('tax_rate', parseFloat(e.target.value) || 0)}
                    placeholder="15"
                  />
                </div>

                {/* US State Tax Selector - only for US region */}
                {companySettings.region === 'US' && usaStates.length > 0 && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" /> State Tax
                    </Label>
                    <Select value={companySettings.tax_state_code || ''} onValueChange={handleStateTaxChange}>
                      <SelectTrigger data-testid="us-state-tax-select">
                        <SelectValue placeholder="Select state..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {usaStates.map(state => (
                          <SelectItem key={state.code} value={state.code}>
                            <span className="flex items-center justify-between w-full gap-2">
                              <span>{state.name}</span>
                              <span className="text-xs text-muted-foreground">{state.rate}%</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      State tax rate auto-updates when you select a state. This rate is used in POS.
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>{isRTL ? 'اسم الضريبة' : 'Tax Name'}</Label>
                  <Select value={companySettings.tax_name} onValueChange={v => updateCompanySetting('tax_name', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="VAT">{isRTL ? 'ضريبة القيمة المضافة (VAT)' : 'Value Added Tax (VAT)'}</SelectItem>
                      <SelectItem value="GST">{isRTL ? 'ضريبة السلع والخدمات (GST)' : 'Goods & Services Tax (GST)'}</SelectItem>
                      <SelectItem value="Sales Tax">{isRTL ? 'ضريبة المبيعات' : 'Sales Tax'}</SelectItem>
                      <SelectItem value="None">{isRTL ? 'بدون ضريبة' : 'No Tax'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><FileText className="h-4 w-4" /> {isRTL ? 'الرقم الضريبي' : 'Tax Number (VAT ID)'}</Label>
                  <Input 
                    value={companySettings.tax_number}
                    onChange={e => updateCompanySetting('tax_number', e.target.value)}
                    placeholder="3XXXXXXXXXX00003"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Document Language Settings */}
            <Card>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <FileText className="h-5 w-5" />
                  {isRTL ? 'إعدادات المستندات' : 'Document Settings'}
                </CardTitle>
                <CardDescription>
                  {isRTL 
                    ? 'تحديد لغة المستندات المطبوعة (الفواتير، أوامر الشراء، عروض الأسعار)'
                    : 'Configure printed document language (Invoices, POs, Quotations)'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Languages className="h-4 w-4" />
                    {isRTL ? 'لغة المستندات' : 'Document Language'}
                  </Label>
                  <Select value={companySettings.document_language} onValueChange={v => updateCompanySetting('document_language', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">
                        {isRTL ? 'English فقط' : 'English Only'}
                      </SelectItem>
                      <SelectItem value="ar">
                        {isRTL ? 'عربي فقط' : 'Arabic Only (عربي فقط)'}
                      </SelectItem>
                      <SelectItem value="bilingual_en">
                        {isRTL ? 'ثنائي اللغة (انجليزي أساسي)' : 'Bilingual - English Primary'}
                      </SelectItem>
                      <SelectItem value="bilingual_ar">
                        {isRTL ? 'ثنائي اللغة (عربي أساسي)' : 'Bilingual - Arabic Primary (عربي أساسي)'}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {isRTL 
                      ? 'للمنطقة العربية، يُنصح باستخدام ثنائي اللغة للامتثال لمتطلبات ZATCA'
                      : 'For KSA/UAE region, bilingual is recommended for ZATCA compliance'}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{isRTL ? 'العنوان (بالعربية)' : 'Address (Arabic)'}</Label>
                    <Input 
                      value={companySettings.address_ar}
                      onChange={e => updateCompanySetting('address_ar', e.target.value)}
                      placeholder="العنوان بالعربية"
                      className="font-arabic text-right"
                      dir="rtl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{isRTL ? 'المدينة (بالعربية)' : 'City (Arabic)'}</Label>
                    <Input 
                      value={companySettings.city_ar}
                      onChange={e => updateCompanySetting('city_ar', e.target.value)}
                      placeholder="المدينة بالعربية"
                      className="font-arabic text-right"
                      dir="rtl"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{isRTL ? 'الشروط والأحكام (انجليزي)' : 'Terms & Conditions (English)'}</Label>
                  <Textarea 
                    value={companySettings.terms_conditions}
                    onChange={e => updateCompanySetting('terms_conditions', e.target.value)}
                    placeholder="Payment is due within 30 days..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{isRTL ? 'الشروط والأحكام (عربي)' : 'Terms & Conditions (Arabic)'}</Label>
                  <Textarea 
                    value={companySettings.terms_conditions_ar}
                    onChange={e => updateCompanySetting('terms_conditions_ar', e.target.value)}
                    placeholder="الدفع مستحق خلال 30 يوماً..."
                    rows={3}
                    className="font-arabic text-right"
                    dir="rtl"
                  />
                </div>

                {/* Document Preview */}
                <div className="p-4 border rounded-lg bg-muted/30">
                  <p className="text-sm font-medium mb-2">{isRTL ? 'معاينة عنوان الفاتورة:' : 'Invoice Header Preview:'}</p>
                  <div className={`p-3 bg-white rounded border ${companySettings.document_language.includes('ar') ? 'font-arabic text-right' : ''}`}
                       dir={companySettings.document_language === 'ar' || companySettings.document_language === 'bilingual_ar' ? 'rtl' : 'ltr'}>
                    <p className="font-bold text-lg">
                      {companySettings.document_language === 'en' && 'Invoice'}
                      {companySettings.document_language === 'ar' && 'فاتورة'}
                      {companySettings.document_language === 'bilingual_en' && 'Invoice / فاتورة'}
                      {companySettings.document_language === 'bilingual_ar' && 'فاتورة / Invoice'}
                    </p>
                    <p className="text-sm">
                      {companySettings.document_language === 'en' && (companySettings.company_name || 'Company Name')}
                      {companySettings.document_language === 'ar' && (companySettings.company_name_ar || 'اسم الشركة')}
                      {companySettings.document_language === 'bilingual_en' && `${companySettings.company_name || 'Company Name'} / ${companySettings.company_name_ar || 'اسم الشركة'}`}
                      {companySettings.document_language === 'bilingual_ar' && `${companySettings.company_name_ar || 'اسم الشركة'} / ${companySettings.company_name || 'Company Name'}`}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Preview */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>{isRTL ? 'معاينة الإعدادات' : 'Settings Preview'}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {isRTL 
                  ? `العرض حسب اللغة المختارة: ${companySettings.language === 'ar' ? 'عربي (رموز)' : 'English (codes)'}`
                  : `Display based on selected language: ${companySettings.language === 'ar' ? 'Arabic (symbols)' : 'English (codes)'}`
                }
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="p-4 bg-muted rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">{isRTL ? 'العملة' : 'Currency'}</p>
                  <p className="text-2xl font-bold">{getCurrencyDisplay(companySettings.currency)} 1,000.00</p>
                  <p className="text-xs">{companySettings.currency} - {getCurrencySymbol(companySettings.currency)}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">{isRTL ? 'الضريبة' : 'Tax'}</p>
                  <p className="text-2xl font-bold">{companySettings.tax_rate}%</p>
                  <p className="text-xs">{companySettings.tax_name}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">{isRTL ? 'المنطقة' : 'Region'}</p>
                  <p className="text-2xl font-bold">{companySettings.region}</p>
                  <p className="text-xs">{regions.find(r => r.code === companySettings.region)?.[isRTL ? 'nameAr' : 'name']}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">{isRTL ? 'اللغة' : 'Language'}</p>
                  <p className="text-2xl font-bold">{companySettings.language.toUpperCase()}</p>
                  <p className="text-xs">{languages.find(l => l.code === companySettings.language)?.[isRTL ? 'nameAr' : 'name']}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className={`flex pt-4 ${isRTL ? 'justify-start' : 'justify-end'}`}>
            <Button onClick={handleSaveCompanySettings} disabled={savingCompany}>
              {savingCompany ? (isRTL ? 'جاري الحفظ...' : 'Saving...') : (isRTL ? 'حفظ الإعدادات الإقليمية' : 'Save Regional Settings')}
            </Button>
          </div>
        </TabsContent>

        {/* AI Voice API Settings - Conditionally shown */}
        {isModuleEnabled('aiVoiceApi') && (
        <TabsContent value="api">
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Mic className="h-5 w-5 text-primary" /> 
                {isRTL ? 'تكوين صوت AI' : 'AI Voice Configuration'}
              </CardTitle>
              <CardDescription>
                {isRTL ? 'تكوين بيانات الاعتماد لميزات Voice AI وLLM' : 'Configure credentials for Voice AI and LLM features.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Retell AI API Key</label>
                <div className="flex gap-2">
                  <Input 
                    type="password" 
                    placeholder="key_..." 
                    value={keys.retell_api_key}
                    onChange={(e) => setKeys({...keys, retell_api_key: e.target.value})}
                  />
                  {status.retell_key_set ? (
                    <div className="flex items-center gap-2 text-green-600 px-3 bg-green-50 border border-green-200 rounded-md whitespace-nowrap">
                      <CheckCircle2 className="h-4 w-4" /> {isRTL ? 'متصل' : 'Connected'}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-600 px-3 bg-amber-50 border border-amber-200 rounded-md whitespace-nowrap">
                      <AlertCircle className="h-4 w-4" /> {isRTL ? 'مفقود' : 'Missing'}
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {isRTL ? 'مطلوب لإجراء مكالمات هاتفية فعلية عبر علامة التبويب الحملات' : 'Required for making actual phone calls via the Campaigns tab.'}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">OpenAI / Emergent Key</label>
                <div className="flex gap-2">
                  <Input 
                    type="password" 
                    placeholder="sk-..." 
                    value={keys.openai_api_key}
                    onChange={(e) => setKeys({...keys, openai_api_key: e.target.value})}
                  />
                  {status.openai_key_set ? (
                    <div className="flex items-center gap-2 text-green-600 px-3 bg-green-50 border border-green-200 rounded-md whitespace-nowrap">
                      <CheckCircle2 className="h-4 w-4" /> {isRTL ? 'متصل' : 'Connected'}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-600 px-3 bg-amber-50 border border-amber-200 rounded-md whitespace-nowrap">
                      <AlertCircle className="h-4 w-4" /> {isRTL ? 'مفقود' : 'Missing'}
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {isRTL ? 'يستخدم لعقل الذكاء العصبي (صياغة المقترحات، نصوص الصوت)' : 'Used for the Neural Intelligence brain (Proposal drafting, Voice scripts).'}
                </p>
              </div>

              <div className="pt-4">
                <Button onClick={handleSaveKeys} disabled={loading}>
                  {loading ? (isRTL ? "جاري الحفظ..." : "Saving...") : (isRTL ? "حفظ التكوين" : "Save Configuration")}
                </Button>
              </div>

            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default Settings;
